import { createHttp } from './http';
import type {
  WbAdminAccount,
  WbAdminAccountCreatePayload,
  WbAdminAccountListResp,
  WbAdminAccountRevokeResp,
  WbAdminAccountUpdatePayload,
  WbAdminLoginResp,
  WbAdminStats,
  WbAdminUser,
  WbAdminUserListResp,
  WbAdminUserResetTrialResp,
  WbAdminUserUpdatePayload,
  WbAuthResp,
  WbInviteCode,
  WbInviteCodeCreatePayload,
  WbInviteCodeListResp,
  WbInviteCodeRevokeResp,
  WbInviteCodeUpdatePayload,
  WbLandingConfig,
  WbLogoutResp,
  WbOcrAvailability,
  WbOcrResp,
  WbProvidersResp,
  WbResumeResp,
  WbStage1Resp,
  WbStage2Resp,
  WbStudyPlanResp,
  WbTaskStatus,
  WbUserData,
  WbUserDataMerge,
  WbUserDataUpdate,
  WbUser,
  WbUserQuota,
} from '@/types/api';

/**
 * web-feedback API 客户端
 *
 * baseURL 来自 VITE_API_WB_BASE，dev 走 /wb-api 代理到 localhost:8000，
 * prod 由 nginx 反代到 https://lihanlin.cn/wb-api。
 *
 * 路径来源：`doc/wb-routes.md`（基于 web-feedback @ commit 6fbe71b）。
 * 重要校准（与 PRD 第 3.2 节的差异）：
 * - 后端**不**使用 ApiResponse 包装，方法返回类型即业务对象
 * - stage1 / stage2 / study-plan / OCR / resume/parse 均强制 multipart/form-data
 * - 历史记录无独立路由，走 /api/user/data* 整体同步
 * - Admin 账号管理路径是 /api/admin/admin-accounts（双重 admin）
 *
 * 调用约定：
 * - 所有方法返回 `Promise<AxiosResponse<T>>`，由调用方 `.data` 取业务对象
 *   （http.ts 拦截器宽松模式不 unwrap）
 * - 401 / 403 由 http.ts 拦截器统一处理（跳登录页 / ElMessage）
 */
const http = createHttp(import.meta.env.VITE_API_WB_BASE);

/**
 * 把对象转为 FormData（仅平铺一层，跳过 undefined / null 字段）。
 *
 * 用于 stage1 / stage2 / study-plan / OCR / resume/parse 等 multipart 路由。
 * - 字符串 / 数字 / 布尔 → 直接 append（axios 会自动 toString）
 * - File / Blob → 直接 append
 * - 其他对象 → JSON.stringify 后 append
 */
function toFormData(payload: Record<string, unknown>): FormData {
  const form = new FormData();
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (value instanceof File || value instanceof Blob) {
      form.append(key, value);
    } else if (typeof value === 'object') {
      form.append(key, JSON.stringify(value));
    } else {
      form.append(key, String(value));
    }
  }
  return form;
}

// ===== 鉴权（wb-routes.md 第 3.1 / 3.2 / 3.3 节 + 4.6 节）=====

export const authApi = {
  /**
   * 邀请码登录。
   *
   * - 路由：`POST /api/auth/invite`（wb-routes.md 第 3.1 节）
   * - 鉴权：**无**（登录入口）
   * - 限流：IP 维度 10/60s + 5 次失败锁 900s
   * - 入参：`{ code }`（**PRD 假设的 `client_id` 字段已废弃**，邀请码 = 用户一对一）
   * - 出参：`WbAuthResp { token, user: WbUser }`（实际响应 user 仅含 id/auth_type/name/email，
   *   TypeScript 层标完整 WbUser，方便调用方写入 store，由后续 fetchMe 补全字段）
   *
   * @param code 邀请码（区分大小写，仅 strip 不 lower；支持 `WFB-XXXXXX` 与 32 位 hex）
   */
  loginByInvite(code: string) {
    // audit T-6 / M-1 修复（2026-08-13）：删除 clientId 废弃兼容参数。
    // 后端 wb-routes.md §3.1 / §6.2 校准后已忽略 client_id 字段，
    // stores/auth.ts:143 早已不传，注释保留过渡期说明但参数已无意义。
    return http.post<WbAuthResp>('/api/auth/invite', { code });
  },

  /**
   * 获取当前登录用户。
   *
   * - 路由：`GET /api/auth/me`（wb-routes.md 第 3.2 节）
   * - 鉴权：**强制**（401 detail.code = 'NO_AUTH'）
   * - 入参：无（仅 JWT header）
   * - 出参：扁平结构 WbUser（不是 PRD 假设的 `{ user: {...} }` 包裹）
   */
  me() {
    return http.get<WbUser>('/api/auth/me');
  },

  /**
   * 注销当前 token。
   *
   * - 路由：`POST /api/auth/logout`（wb-routes.md 第 3.3 节）
   * - 鉴权：**无 Depends**（语义无害，前端清 token 即可）
   * - 出参：`{ ok: true }`（后端无状态，仅占位）
   */
  logout() {
    return http.post<WbLogoutResp>('/api/auth/logout');
  },

  /**
   * 管理员账号密码登录（独立路径）。
   *
   * - 路由：`POST /api/auth/admin-login`（wb-routes.md 第 4.6 节）
   * - 鉴权：**无**（登录入口）
   * - 限流：IP 维度 10/60s + 5 次失败锁 900s
   * - 入参：`{ username, password }`（**不是邀请码**）
   * - 出参：`{ token, redirect: '/admin.html', user: { id, username } }`
   */
  adminLogin(payload: { username: string; password: string }) {
    return http.post<WbAdminLoginResp>('/api/auth/admin-login', payload);
  },
} as const;

// ===== 主分析流程（两段串行，wb-routes.md 第 3.4 / 3.5 / 4.1 节）=====

export const analyzeApi = {
  /**
   * Stage1：拆解岗位要求。
   *
   * - 路由：`POST /api/analyze/requirements`（wb-routes.md 第 3.4 节）
   * - 鉴权：**强制** + 配额校验 + 扣减
   * - 限流：60/60s（IP+user_id）
   * - 入参：**强制 multipart/form-data**
   *   - `jd_text`（必填，50-10000 字，**字段名不是 jd**）
   *   - `provider` / `model` / `task_id` 可选（断点续跑场景）
   * - 出参：`WbStage1Resp`（若传 task_id 额外返回 task_id 字段）
   *
   * 关键校准：
   * - PRD 假设的 `jd` 字段名错误，实际是 `jd_text`
   * - PRD 假设的 stage1 入参含 `resume_text` / `resume_file` **不正确**，stage1 不传简历
   * - 强制 multipart，**不支持 JSON**
   */
  stage1Requirements(payload: {
    jd_text: string;
    provider?: string;
    model?: string;
    task_id?: string;
  }) {
    const form = toFormData(payload as Record<string, unknown>);
    return http.post<WbStage1Resp>('/api/analyze/requirements', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Stage2：匹配分析 + 面试题。
   *
   * - 路由：`POST /api/analyze/match`（wb-routes.md 第 3.5 节）
   * - 鉴权：**强制** + 配额校验 + 扣减
   * - 入参：**强制 multipart/form-data**
   *   - `requirements_json`（**必填**，stage1 返回的 `requirements` 对象的 JSON 字符串，2-20000 字）
   *   - 简历来源三选一（必须自行提供简历，PRD 假设的「Stage2 仅传 task_id」不存在）：
   *     - `resume_text`（≤20000 字）
   *     - `resume_file`（UploadFile）
   *     - `use_default_resume`（bool）
   *   - `provider` / `model` / `task_id` 可选
   * - 出参：`WbStage2Resp`（若传 task_id 额外返回 task_id 字段）
   *
   * 关键校准：PRD 假设的 `{ task_id }` 完全错误。
   */
  stage2Match(payload: {
    requirements_json: string;
    resume_text?: string;
    resume_file?: File;
    use_default_resume?: boolean;
    provider?: string;
    model?: string;
    task_id?: string;
  }) {
    const form = toFormData(payload as Record<string, unknown>);
    return http.post<WbStage2Resp>('/api/analyze/match', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * 任务状态轮询（断点续跑）。
   *
   * - 路由：`GET /api/analyze/status/:task_id`（wb-routes.md 第 4.1 节）
   * - 鉴权：**强制**（task 必须属于当前 user）
   * - 出参：`WbTaskStatus`
   *
   * 关键校准：PRD 假设的 `GET /api/tasks/:id` 路径错误。
   */
  pollTask(taskId: string) {
    return http.get<WbTaskStatus>(`/api/analyze/status/${taskId}`);
  },

  /**
   * 生成学习计划（W1 占位，按钮 disabled）。
   *
   * - 路由：`POST /api/analyze/study-plan`（wb-routes.md 第 4.2 节）
   * - 鉴权：**强制** + 配额校验 + 扣减 + 写入 `UserData.study_plans[history_id]`
   * - 入参：**强制 multipart/form-data**
   *   - `match_json` / `requirements_json`（必填）
   *   - `history_id`（必填，且必须属于当前用户）
   *   - 简历来源三选一同 stage2
   *   - `provider` / `model` 可选
   * - 出参：`WbStudyPlanResp`
   *
   * 关键校准：PRD 假设的 `POST /api/study-plan { task_id }` 路径和入参都错。
   */
  generateStudyPlan(payload: {
    match_json: string;
    requirements_json: string;
    history_id: string;
    resume_text?: string;
    resume_file?: File;
    use_default_resume?: boolean;
    provider?: string;
    model?: string;
  }) {
    const form = toFormData(payload as Record<string, unknown>);
    return http.post<WbStudyPlanResp>('/api/analyze/study-plan', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
} as const;

// ===== JD OCR（截图识别）+ 简历解析（wb-routes.md 第 4.3 节）=====

export const ocrApi = {
  /**
   * JD 截图 OCR 识别。
   *
   * - 路由：`POST /api/analyze/ocr`（wb-routes.md 第 4.3 节）
   * - 鉴权：**强制** + 限流 30/60s
   * - 入参：**multipart/form-data**，`file: UploadFile`（png/jpeg/webp/gif，≤5MB）
   * - 出参：`WbOcrResp { text, chars }`
   *
   * 关键校准：PRD 假设的 `POST /api/ocr { image: base64 }` 完全错误。
   */
  recognize(file: File) {
    const form = new FormData();
    form.append('file', file);
    return http.post<WbOcrResp>('/api/analyze/ocr', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * 查询 OCR 服务可用性（决定 Landing 页是否显示「上传 JD 截图」入口）。
   *
   * - 路由：`GET /api/analyze/ocr/availability`（wb-routes.md 第 4.3 节）
   * - 鉴权：**无**（公开）
   * - 出参：`{ available: boolean }`
   */
  availability() {
    return http.get<WbOcrAvailability>('/api/analyze/ocr/availability');
  },
} as const;

export const resumeApi = {
  /**
   * 简历解析。
   *
   * - 路由：`POST /api/resume/parse`（wb-routes.md 第 4.3 节）
   * - 鉴权：**强制**
   * - 入参：multipart `file: UploadFile`（≤5MB）
   * - 出参：`WbResumeResp { text, chars }`
   */
  parse(file: File) {
    const form = new FormData();
    form.append('file', file);
    return http.post<WbResumeResp>('/api/resume/parse', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
} as const;

// ===== 用户数据云同步（替换老 historyApi，wb-routes.md 第 3.6 节）=====
//
// 重要：web-feedback 后端没有 /api/history 路由，历史记录通过整体
// /api/user/data* 同步实现。前端在 localStorage 维护 history 数组。
//
// 老前端 historyApi.remove(id) 已**删除**（后端无单条删除路由），
// 需要删除单条历史时由前端 GET + 过滤 + PUT 实现（见 removeHistory）。

export const userDataApi = {
  /**
   * 读取云端数据（含历史 / 学习计划 / 简历）。
   *
   * - 路由：`GET /api/user/data`（wb-routes.md 第 3.6 节）
   * - 鉴权：**强制**
   * - 出参：`WbUserData { history, study_plans, resumes, updated_at }`
   *
   * 替换老 historyApi.list()（历史走整体同步）。
   */
  get() {
    return http.get<WbUserData>('/api/user/data');
  },

  /**
   * 整体覆盖云端数据（None 字段保留原值）。
   *
   * - 路由：`PUT /api/user/data`（wb-routes.md 第 3.6 节）
   * - 鉴权：**强制**
   */
  update(payload: WbUserDataUpdate) {
    return http.put<WbUserData>('/api/user/data', payload);
  },

  /**
   * 客户端上传合并（client 覆盖 server）。
   *
   * - 路由：`POST /api/user/data/merge`（wb-routes.md 第 3.6 节）
   * - 鉴权：**强制**
   *
   * 替换老 historyApi.upsert(entry)（单条历史改为 merge 整个 history 数组）。
   */
  merge(payload: WbUserDataMerge) {
    return http.post<WbUserData>('/api/user/data/merge', payload);
  },

  /**
   * 删除单条历史记录（前端实现：GET → 过滤 → PUT）。
   *
   * 后端 wb-routes.md 第 3.6 节明确无 `DELETE /api/history/:id` 路由，
   * 必须在前端组装三步操作。调用方需处理并发冲突（建议失败时重新拉取）。
   */
  async removeHistory(historyId: string): Promise<WbUserData> {
    const resp = await this.get();
    const data = resp.data;
    const filtered = data.history.filter((h) => h.id !== historyId);
    const updateResp = await this.update({ history: filtered });
    return updateResp.data;
  },
} as const;

// ===== 配额查询（wb-routes.md 第 4.4 节）=====

export const quotaApi = {
  /**
   * 查询当前用户配额。
   *
   * - 路由：`GET /api/user/quota`（wb-routes.md 第 4.4 节）
   * - 鉴权：**强制** + 限流 60/60s
   * - 出参：`WbUserQuota`（total=-1 表示无限）
   *
   * 用于前端展示剩余次数徽章（替代 authApi.me 中已废弃的 trial_remaining）。
   */
  get() {
    return http.get<WbUserQuota>('/api/user/quota');
  },
} as const;

// ===== Provider 配置 + Landing 配置（wb-routes.md 第 4.8 节 + 第 5.2 节）=====

export const configApi = {
  /**
   * Provider 配置。
   *
   * - 路由：`GET /api/providers`（wb-routes.md 第 4.8 节）
   * - 鉴权：**无**（公开）
   * - 出参：`WbProvidersResp`（注意 `default` 是 JS 保留字，访问用 `resp.data['default']`）
   */
  providers() {
    return http.get<WbProvidersResp>('/api/providers');
  },

  /**
   * Landing 页 Footer 配置（作者信息）。
   *
   * - 路由：`GET /api/landing-config`（wb-routes.md 第 5.2 节）
   * - 鉴权：**无**（公开）
   *
   * W1-T4 已被 `src/composables/usePortalData.ts` 消费，签名保持不变。
   */
  landingConfig() {
    return http.get<WbLandingConfig>('/api/landing-config');
  },
} as const;

// ===== Admin 邀请码管理（wb-routes.md 第 4.5 节）=====

export const adminApi = {
  // ----- 邀请码 -----

  /**
   * 列出邀请码。
   *
   * - 路由：`GET /api/admin/invite-codes`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   * - 出参：**包裹结构** `WbInviteCodeListResp { total, codes: [...] }`（不是裸数组）
   */
  listInviteCodes() {
    return http.get<WbInviteCodeListResp>('/api/admin/invite-codes');
  },

  /**
   * 创建邀请码（**超管 lihanlin 独占**，普通 admin 调用 403）。
   *
   * - 路由：`POST /api/admin/invite-codes`（wb-routes.md 第 4.5 节）
   * - 鉴权：**超管独占**
   * - 出参：单个 `WbInviteCode` 对象
   */
  createInviteCode(payload: WbInviteCodeCreatePayload) {
    return http.post<WbInviteCode>('/api/admin/invite-codes', payload);
  },

  /**
   * 查询单个邀请码。
   *
   * - 路由：`GET /api/admin/invite-codes/{code}`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   * - 入参：path 参数是 code 字符串（UUID hex / `WFB-XXXXXX`），**不是数字 id**
   */
  getInviteCode(code: string) {
    return http.get<WbInviteCode>(`/api/admin/invite-codes/${encodeURIComponent(code)}`);
  },

  /**
   * 更新邀请码。
   *
   * - 路由：`PATCH /api/admin/invite-codes/{code}`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   * - 入参：path 参数 code 字符串 + body 部分字段
   */
  updateInviteCode(code: string, payload: WbInviteCodeUpdatePayload) {
    return http.patch<WbInviteCode>(
      `/api/admin/invite-codes/${encodeURIComponent(code)}`,
      payload,
    );
  },

  /**
   * 撤销邀请码。
   *
   * - 路由：`DELETE /api/admin/invite-codes/{code}`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   * - 入参：path 参数是 code 字符串（**不是数字 id**）
   * - 出参：`{ ok, is_active: false }`
   */
  revokeInviteCode(code: string) {
    return http.delete<WbInviteCodeRevokeResp>(
      `/api/admin/invite-codes/${encodeURIComponent(code)}`,
    );
  },

  // ----- 管理员账号管理（admin-accounts，wb-routes.md 第 4.5 节「账号管理」）-----

  /**
   * 列出管理员账号。
   *
   * - 路由：`GET /api/admin/admin-accounts`（注意**双重 admin**，wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   * - 出参：**包裹结构** `WbAdminAccountListResp { total, accounts: [...] }`
   */
  listAccounts(query?: { include_inactive?: boolean; include_revoked?: boolean }) {
    return http.get<WbAdminAccountListResp>('/api/admin/admin-accounts', {
      params: query,
    });
  },

  /**
   * 创建管理员账号。
   *
   * - 路由：`POST /api/admin/admin-accounts`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   */
  createAccount(payload: WbAdminAccountCreatePayload) {
    return http.post<WbAdminAccount>('/api/admin/admin-accounts', payload);
  },

  /**
   * 更新管理员账号（支持停启用 + 重置密码，含防自杀/防锁死保护）。
   *
   * - 路由：`PATCH /api/admin/admin-accounts/{account_id}`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   * - 入参：path 参数 account_id 是 **int**（PRD 早期假设的 string 错误）
   *
   * 替换老 toggleAccountActive(id: string, active)（id 类型 string → number，
   * 且支持重置密码）。
   */
  updateAccount(accountId: number, payload: WbAdminAccountUpdatePayload) {
    return http.patch<WbAdminAccount>(
      `/api/admin/admin-accounts/${accountId}`,
      payload,
    );
  },

  /**
   * 撤销管理员账号（含防自杀/防锁死保护）。
   *
   * - 路由：`DELETE /api/admin/admin-accounts/{account_id}`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   */
  revokeAccount(accountId: number) {
    return http.delete<WbAdminAccountRevokeResp>(
      `/api/admin/admin-accounts/${accountId}`,
    );
  },

  // ----- 用户管理（admin/users，与 admin-accounts 不同）-----

  /**
   * 列出用户（wb-routes.md 第 4.5 节「用户管理」）。
   *
   * - 路由：`GET /api/admin/users`
   * - 鉴权：**admin 强制**
   * - 出参：**包裹结构** `WbAdminUserListResp { total, users: [...] }`
   */
  listUsers() {
    return http.get<WbAdminUserListResp>('/api/admin/users');
  },

  /**
   * 更新用户（停启用 / 改 trial_quota / 改 role，含防提权/防锁死保护）。
   *
   * - 路由：`PUT /api/admin/users/{user_id}`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   * - 入参：path 参数 user_id 是 **int**
   */
  updateUser(userId: number, payload: WbAdminUserUpdatePayload) {
    return http.put<WbAdminUser>(`/api/admin/users/${userId}`, payload);
  },

  /**
   * 重置用户试用配额。
   *
   * - 路由：`POST /api/admin/users/{user_id}/reset-trial`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   * - 出参：`{ ok, trial_used: 0 }`
   */
  resetUserTrial(userId: number) {
    return http.post<WbAdminUserResetTrialResp>(
      `/api/admin/users/${userId}/reset-trial`,
    );
  },

  /**
   * Admin 面板统计卡数据。
   *
   * - 路由：`GET /api/admin/stats`（wb-routes.md 第 4.5 节）
   * - 鉴权：**admin 强制**
   */
  stats() {
    return http.get<WbAdminStats>('/api/admin/stats');
  },
} as const;

// ===== 向后兼容导出（仅类型别名，不破坏现有调用点）=====
//
// 老前端 PRD 假设的 historyApi 已删除（后端无 /api/history 路由），
// 调用方迁移到 userDataApi（见上）。W1-T5 各 view 实现时统一替换。
